import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Box,
  Building2,
  Globe,
  Calculator,
  Users,
  Inbox,
  Plus,
  Star,
  RefreshCw,
  EyeOff,
  Eye,
  Server,
  ChevronRight,
} from "lucide-react";
import api from "../lib/api";
import { cn } from "../lib/utils";

const PRODUCT_ICON_MAP = { Building2, Globe, Calculator, Users, Inbox, Box };
const ICON_OPTIONS = ["Building2", "Globe", "Calculator", "Users", "Inbox", "Box"];
const COLOR_OPTIONS = ["orange", "amber", "yellow", "blue", "emerald", "violet", "zinc"];

const PRODUCT_COLOR_CLASSES = {
  orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  zinc: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40",
};
const productClass = (color) => PRODUCT_COLOR_CLASSES[color] || PRODUCT_COLOR_CLASSES.zinc;

const emptyNewForm = {
  slug: "",
  name: "",
  short_label: "",
  icon: "Box",
  color: "zinc",
};

const SupportProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newForm, setNewForm] = useState(emptyNewForm);
  const [creating, setCreating] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      // active_only=false so admins see deactivated products too.
      const { data } = await api.get("/support-products", { params: { active_only: false } });
      setProducts(data || []);
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openProduct = (slug) => navigate(`/settings/support-products/${slug}`);

  const createProduct = async () => {
    if (!newForm.slug.trim() || !newForm.name.trim()) {
      toast.error("Slug and name are required");
      return;
    }
    if (!/^[a-z0-9_\-]+$/.test(newForm.slug.trim())) {
      toast.error("Slug must be lowercase letters, digits, _ or -");
      return;
    }
    try {
      setCreating(true);
      const slug = newForm.slug.trim().toLowerCase();
      await api.post("/support-products", {
        slug,
        name: newForm.name.trim(),
        short_label: newForm.short_label.trim() || newForm.name.trim(),
        icon: newForm.icon,
        color: newForm.color,
        is_active: true,
        sort_order: 50,
      });
      toast.success("Product created — fill in the rest on the next page");
      setNewDialogOpen(false);
      setNewForm(emptyNewForm);
      navigate(`/settings/support-products/${slug}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (e, p) => {
    e.stopPropagation();
    try {
      if (p.is_active) {
        await api.delete(`/support-products/${p.slug}`);
        toast.success("Deactivated");
      } else {
        await api.put(`/support-products/${p.slug}`, { is_active: true });
        toast.success("Reactivated");
      }
      fetchProducts();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="space-y-5 p-6" data-testid="support-products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Box className="w-5 h-5 text-orange-400" /> Support Products
          </h1>
          <p className="text-sm text-zinc-500">
            Multi-tenant platforms users can scope their support chat to (HUB, Spinhouse, Vexos).
            Click a product to manage deployments, mirrors, and AI context.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-zinc-700" onClick={fetchProducts}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => { setNewForm(emptyNewForm); setNewDialogOpen(true); }}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="product-new"
          >
            <Plus className="w-4 h-4 mr-1" /> New product
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {loading ? (
          <p className="text-sm text-zinc-500 text-center py-8">Loading…</p>
        ) : products.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-8">No products yet.</p>
        ) : products.map((p) => {
          const Icon = PRODUCT_ICON_MAP[p.icon] || Box;
          const totalInstances = (p.instances || []).length;
          const activeInstances = (p.instances || []).filter((i) => i.is_active !== false).length;
          const totalMirrors = (p.instances || []).reduce(
            (sum, i) => sum + (i.mirrors || []).filter((m) => m.is_active !== false).length,
            0
          );
          return (
            <Card
              key={p.slug}
              className={cn(
                "bg-zinc-900/40 border-zinc-800 hover:border-zinc-700 transition-all cursor-pointer",
                !p.is_active && "opacity-50"
              )}
              onClick={() => openProduct(p.slug)}
              data-testid={`product-${p.slug}`}
            >
              <CardContent className="p-3.5 flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center border", productClass(p.color))}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-medium text-zinc-100 truncate">{p.name}</h3>
                    <span className="text-[10px] text-zinc-500 font-mono">{p.slug}</span>
                    {p.is_default && (
                      <Badge variant="outline" className="text-[9px] border-orange-500/40 text-orange-300">
                        <Star className="w-2.5 h-2.5 mr-0.5" /> default
                      </Badge>
                    )}
                    {!p.is_active && (
                      <Badge variant="outline" className="text-[9px] border-zinc-600 text-zinc-500">inactive</Badge>
                    )}
                  </div>
                  {p.description && <p className="text-xs text-zinc-500 mt-0.5 truncate">{p.description}</p>}
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {p.url && <p className="text-[10px] text-zinc-600 truncate font-mono">{p.url}</p>}
                    {totalInstances > 0 && (
                      <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                        <Server className="w-3 h-3" />
                        {activeInstances}/{totalInstances} deployment(s)
                        {totalMirrors > 0 && (
                          <span className="text-zinc-600 ml-1">· {totalMirrors} mirror(s)</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => toggleActive(e, p)}
                    title={p.is_active ? "Deactivate" : "Reactivate"}
                  >
                    {p.is_active
                      ? <EyeOff className="w-3.5 h-3.5 text-zinc-400 hover:text-red-300" />
                      : <Eye className="w-3.5 h-3.5 text-zinc-400 hover:text-emerald-300" />}
                  </Button>
                  <ChevronRight className="w-4 h-4 text-zinc-500" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Minimal new-product dialog: just enough to create the row, then we
          redirect to the detail page where the rest can be filled in. */}
      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Box className="w-4 h-4 text-orange-400" /> New product
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Slug</label>
              <Input
                value={newForm.slug}
                onChange={(e) => setNewForm((s) => ({ ...s, slug: e.target.value }))}
                placeholder="vexos"
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 font-mono"
                data-testid="new-product-slug"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Stable identifier. Cannot be changed later.</p>
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Display name</label>
              <Input
                value={newForm.name}
                onChange={(e) => setNewForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="Vexos"
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
                data-testid="new-product-name"
              />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Short label</label>
              <Input
                value={newForm.short_label}
                onChange={(e) => setNewForm((s) => ({ ...s, short_label: e.target.value }))}
                placeholder="Vexos"
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-zinc-500 uppercase">Icon</label>
                <Select value={newForm.icon} onValueChange={(v) => setNewForm((s) => ({ ...s, icon: v }))}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-zinc-500 uppercase">Color</label>
                <Select value={newForm.color} onValueChange={(v) => setNewForm((s) => ({ ...s, color: v }))}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {COLOR_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>
                        <span className={cn("inline-block w-3 h-3 rounded mr-2 border", productClass(c))} />
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700" onClick={() => setNewDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={createProduct}
              disabled={creating || !newForm.slug.trim() || !newForm.name.trim()}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="new-product-create"
            >
              {creating ? "Creating…" : "Create & open"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportProductsPage;
